import React from "react";
import { Navigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { endpoints } from "@/api";
import { useAdmin } from "@/hooks/useAdmin";
import { useAuth } from "@/auth";
import { useI18n } from "@/i18n";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Users, Globe, GraduationCap, MessageSquare, BarChart3, Shield, BookOpen, User, MapPin, Coffee, Scale, ExternalLink, Trophy, Medal } from "lucide-react";
import AdminUsersTab from "./admin/AdminUsersTab";
import AdminCountriesTab from "./admin/AdminCountriesTab";
import AdminAcademyTab from "./admin/AdminAcademyTab";
import AdminCommentsTab from "./admin/AdminCommentsTab";
import AdminAboutTab from "./admin/AdminAboutTab";
import AdminBlogTab from "./admin/AdminBlogTab";
import AdminJourneyTab from "./admin/AdminJourneyTab";
import AdminEncyclopediaTab from "./admin/AdminEncyclopediaTab";
import AdminRecipesTab from "./admin/AdminRecipesTab";
import AdminRatioTab from "./admin/AdminRatioTab";
import AdminChallengeTab from "./admin/AdminChallengeTab";
import AdminAchievementsTab from "./admin/AdminAchievementsTab";

export default function AdminPanel() {
  const { user, loading } = useAuth();
  const isAdmin = useAdmin();
  const { t, isRTL } = useI18n();
  const serifClass = isRTL ? "font-serif-ar" : "font-serif-en";

  const { data: stats } = useQuery({
    queryKey: ["admin", "stats"],
    queryFn: endpoints.adminStats,
    enabled: isAdmin,
    refetchInterval: 30_000,
  });

  if (loading) return <div className="max-w-7xl mx-auto px-6 pt-16">…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (!isAdmin) {
    return (
      <div className="max-w-3xl mx-auto px-6 pt-24 pb-24 text-center" data-testid="admin-forbidden">
        <Shield className="w-12 h-12 mx-auto text-[#C86240] mb-4" />
        <h1 className={`${serifClass} text-3xl text-[#2A2421] mb-2`}>
          {isRTL ? "هذه الصفحة للأدمن فقط" : "Admin access only"}
        </h1>
        <p className="text-[#5C544F]">
          {isRTL ? "ليس لديك صلاحية الدخول إلى لوحة التحكم." : "You don't have permission to view this page."}
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-10 lg:pt-16 pb-24" data-testid="admin-panel">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.3em] text-[#C86240] mb-3">{isRTL ? "لوحة التحكم" : "Admin Console"}</div>
        <h1 className={`${serifClass} text-3xl lg:text-5xl text-[#2A2421] leading-[1.05]`}>
          {isRTL ? "إدارة عبدالله | علم القهوة" : "Manage Abdullah | Coffee Science"}
        </h1>
      </div>

      {/* Stats strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3 mb-8" data-testid="admin-stats-strip">
        <Stat label={isRTL ? "مستخدمون" : "Users"} value={stats?.users ?? "…"} icon={Users} />
        <Stat label={isRTL ? "نشطون أسبوعياً" : "Active 7d"} value={stats?.active_7d ?? "…"} icon={BarChart3} />
        <Stat label={isRTL ? "وصفات" : "Recipes"} value={stats?.recipes_total ?? "…"} icon={GraduationCap} />
        <Stat label={isRTL ? "مقالات" : "Posts"} value={stats?.blog_posts ?? "…"} icon={MessageSquare} />
        <Stat label={isRTL ? "آراء" : "Reviews"} value={stats?.testimonials ?? "…"} icon={MessageSquare} />
        <Stat label={isRTL ? "تعليقات (إجمالي)" : "Comments total"} value={stats?.comments_total ?? "…"} icon={MessageSquare} testid="stat-comments-total" highlight />
        <Stat label={isRTL ? "أكاديمية" : "Academy"} value={stats?.academy_comments ?? "…"} icon={GraduationCap} testid="stat-academy-comments" />
        <Stat label={isRTL ? "موسوعة" : "Encyclopedia"} value={stats?.encyclopedia_comments ?? "…"} icon={BookOpen} testid="stat-encyclopedia-comments" />
      </div>

      <Tabs defaultValue="users" className="w-full">
        <TabsList className="bg-[#F9F6F0] border border-[#2A2421]/10 rounded-full p-1 h-auto flex-wrap" data-testid="admin-tabs">
          <TabTrig value="users" icon={Users} label={isRTL ? "المستخدمون" : "Users"} />
          <TabTrig value="journey" icon={MapPin} label={isRTL ? "المسيرة" : "Journey"} />
          <TabTrig value="countries" icon={Globe} label={isRTL ? "الدول" : "Countries"} />
          <TabTrig value="academy" icon={GraduationCap} label={isRTL ? "الأكاديمية" : "Academy"} />
          <TabTrig value="encyclopedia" icon={BookOpen} label={isRTL ? "الموسوعة" : "Encyclopedia"} />
          <TabTrig value="recipes" icon={Coffee} label={isRTL ? "الوصفات" : "Recipes"} />
          <TabTrig value="ratio" icon={Scale} label={isRTL ? "الريشيو" : "Ratio"} />
          <TabTrig value="challenge" icon={Trophy} label={isRTL ? "التحدي" : "Challenge"} />
          <TabTrig value="achievements" icon={Medal} label={isRTL ? "الشارات" : "Badges"} />
          <TabTrig value="about" icon={User} label={isRTL ? "نبذة" : "About"} />
          <TabTrig value="blog" icon={BookOpen} label={isRTL ? "المدونة" : "Blog"} />
          <TabTrig value="comments" icon={MessageSquare} label={isRTL ? "التعليقات والآراء" : "Comments & Reviews"} />
        </TabsList>

        <TabsContent value="users" className="mt-8"><AdminUsersTab /></TabsContent>
        <TabsContent value="journey" className="mt-8"><PreviewBar route="/journey" label={isRTL ? "معاينة صفحة الرحلة" : "View Journey page"} /><AdminJourneyTab /></TabsContent>
        <TabsContent value="countries" className="mt-8"><PreviewBar route="/countries" label={isRTL ? "معاينة صفحة الدول" : "View Countries page"} /><AdminCountriesTab /></TabsContent>
        <TabsContent value="academy" className="mt-8"><PreviewBar route="/academy" label={isRTL ? "معاينة الأكاديمية" : "View Academy page"} /><AdminAcademyTab /></TabsContent>
        <TabsContent value="encyclopedia" className="mt-8"><PreviewBar route="/encyclopedia" label={isRTL ? "معاينة الموسوعة" : "View Encyclopedia page"} /><AdminEncyclopediaTab /></TabsContent>
        <TabsContent value="recipes" className="mt-8"><PreviewBar route="/recipes" label={isRTL ? "معاينة الوصفات" : "View Recipes page"} /><AdminRecipesTab /></TabsContent>
        <TabsContent value="ratio" className="mt-8"><PreviewBar route="/calculator" label={isRTL ? "معاينة حاسبة الريشيو" : "View Ratio Calculator"} /><AdminRatioTab /></TabsContent>
        <TabsContent value="challenge" className="mt-8"><PreviewBar route="/challenge" label={isRTL ? "معاينة صفحة التحدي" : "View Challenge page"} /><AdminChallengeTab /></TabsContent>
        <TabsContent value="achievements" className="mt-8"><PreviewBar route="/achievements" label={isRTL ? "معاينة صفحة الإنجازات" : "View Achievements page"} /><AdminAchievementsTab /></TabsContent>
        <TabsContent value="about" className="mt-8"><PreviewBar route="/about" label={isRTL ? "معاينة صفحة نبذة" : "View About page"} /><AdminAboutTab /></TabsContent>
        <TabsContent value="blog" className="mt-8"><PreviewBar route="/blog" label={isRTL ? "معاينة المدونة" : "View Blog page"} /><AdminBlogTab /></TabsContent>
        <TabsContent value="comments" className="mt-8"><AdminCommentsTab /></TabsContent>
      </Tabs>
    </div>
  );
}

const Stat = ({ label, value, icon: Icon, testid, highlight }) => (
  <div className={`rounded-2xl border p-3 ${highlight ? "border-[#C86240]/40 bg-[#C86240]/5" : "border-[#2A2421]/10 bg-white"}`} data-testid={testid}>
    <div className={`flex items-center gap-1.5 text-[10px] uppercase tracking-widest ${highlight ? "text-[#C86240]" : "text-[#5C544F]"}`}>
      <Icon className="w-3 h-3" /> {label}
    </div>
    <div className={`text-2xl font-semibold mt-1 ${highlight ? "text-[#C86240]" : "text-[#2A2421]"}`}>{value}</div>
  </div>
);

const TabTrig = ({ value, icon: Icon, label }) => (
  <TabsTrigger
    value={value}
    data-testid={`admin-tab-${value}`}
    className="rounded-full px-4 py-2 data-[state=active]:bg-[#2A2421] data-[state=active]:text-[#F9F6F0] gap-1.5"
  >
    <Icon className="w-3.5 h-3.5" />
    <span>{label}</span>
  </TabsTrigger>
);


const PreviewBar = ({ route, label }) => (
  <a
    href={route}
    target="_blank"
    rel="noopener noreferrer"
    data-testid={`preview-link-${route.replace(/\//g, "")}`}
    className="inline-flex items-center gap-1.5 mb-4 px-3 py-1.5 rounded-full text-xs text-[#C86240] border border-[#C86240]/25 bg-[#C86240]/5 hover:bg-[#C86240]/10 transition-colors"
  >
    <ExternalLink className="w-3 h-3" />
    {label}
  </a>
);
